import { useContext } from 'react'

import {
  Button, FileInput, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCampaignDocument, { useCampaignDocumentForm } from '@hooks/useCampaignDocument'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CampaignSubHeader from '@components/CampaignSubHeader'

const MODAL_KEY = 'CreateCampaignDocumentModal'

type ModalCallbacks = {
  closeModal: () => void,
  createCampaignDocument: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateCampaignDocumentModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createCampaignDocument } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { campaignDocument: initCampaignDocument } = modalPayload

  const {
    callbacks: { createCampaignDocument: createFn },
    creating,
    campaign,
    campaignDocument,
    updating,
  } = useCampaignDocument(initCampaignDocument)

  const {
    entityState,
    entityState: {
      document_type,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useCampaignDocumentForm(campaignDocument, { validateOn: 'change' })

  const createCampaignDocumentPayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Document Created',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Add" titleSecondLine="Document" />

      {/*
       // @ts-expect-error TS2740 */}
      <CampaignSubHeader campaign={campaign} />

      <SidebarModal.Content hasSubHeader>
        <Form>
          <Form.Field label="* File (.pdf, .doc)">
            <FileInput
              input={{
                onChange: (fileData: object, fileObject: object) => (
                  // @ts-expect-error TS2339
                  setEntityState({ file: fileData, file_file_name: fileObject.name })
                ),
              }}
            />
          </Form.Field>

          <Form.Field label="* Title" errorMessage={errors.title} marginTop="large">
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="Document Type" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select
              name="document_type"
              value={document_type}
              {...handlers}
            >
              <option value="">Please Select...</option>
              <option value="building_inspection">Building Inspection</option>
              <option value="sample_contract">Sample Contract</option>
              <option value="statement_of_information">Property Price Statement</option>
              <option value="other">Other</option>
            </select>
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createCampaignDocument(createCampaignDocumentPayload)}
          size="large"
        >
          {saveEnabled ? 'Save Document' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateCampaignDocumentModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
